import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only "Export to Excel" dialog with a column picker.
 * entity: "students" | "teachers"
 * params: extra query params (e.g. { class_name, section }) for students
 */
export default function ExportDialog({ entity, params = {}, disabled = false, buttonLabel = "Export Excel" }) {
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || fields.length) return;
    setLoading(true);
    api.get("/admin/export/fields")
      .then(({ data }) => {
        const cols = data[entity] || [];
        setFields(cols);
        setSelected(Object.fromEntries(cols.map((c) => [c.key, c.default])));
      })
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Failed to load fields"))
      .finally(() => setLoading(false));
  }, [open, entity, fields.length]);

  const toggle = (k) => setSelected((p) => ({ ...p, [k]: !p[k] }));
  const chosen = fields.filter((f) => selected[f.key]).map((f) => f.key);

  const download = async () => {
    if (chosen.length === 0) return toast.error("Select at least one column");
    setBusy(true);
    try {
      const res = await api.get(`/admin/export/${entity}`, {
        params: { ...params, fields: chosen.join(",") },
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : `${entity}.xlsx`;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
      setOpen(false);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Export failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full" disabled={disabled} data-testid={`export-${entity}-btn`}>
          <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" /> {buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`export-${entity}-dialog`}>
        <DialogHeader>
          <DialogTitle>Export {entity} to Excel</DialogTitle>
          <DialogDescription>Choose the columns to include. The sheet includes the school logo &amp; header.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-2 gap-2.5 max-h-72 overflow-y-auto py-2">
            {fields.map((f) => (
              <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`export-field-${f.key}`}>
                <Checkbox checked={!!selected[f.key]} onCheckedChange={() => toggle(f.key)} />
                <span className="text-slate-700 dark:text-slate-200">{f.label}</span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <span className="text-xs text-slate-400 mr-auto self-center">{chosen.length} column{chosen.length === 1 ? "" : "s"} selected</span>
          <Button onClick={download} disabled={busy || chosen.length === 0} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid={`export-${entity}-download`}>
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
